import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Build as BuildIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { workerAPI, userAPI } from "../../utils/api";

const WorkerManagement = () => {
  const { userProfile } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    universityId: "",
    phone: "",
    specialization: "",
    availability: "available",
    department: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const specializations = [
    "Electrical",
    "Plumbing",
    "HVAC",
    "Carpentry",
    "Painting",
    "Cleaning",
    "IT Support",
    "General Maintenance",
    "Equipment Repair",
    "Safety & Security",
    "Electronics",
    "Appliance Repair",
    "Landscaping",
    "Masonry",
    "Roofing",
    "Flooring",
    "Lighting Systems",
    "Fire Safety",
    "Pest Control",
    "Waste Management",
  ];

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await workerAPI.getAllWorkers();
      setWorkers(response.data);
    } catch (error) {
      console.error("Error fetching workers:", error);
      setError("Failed to load workers");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorker = async () => {
    if (
      !formData.name ||
      !formData.email ||
      !formData.universityId ||
      !formData.phone ||
      !formData.specialization
    ) {
      setError("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      // Step 1: Create user account with authentication
      const userData = {
        name: formData.name,
        email: formData.email,
        universityId: formData.universityId,
        password: "ChangeMe123!", // Default password that worker must change
        role: "worker",
        departmentId: userProfile?.departmentId,
        department: userProfile?.department,
      };

      console.log("Creating worker user account...");
      const userResponse = await userAPI.createWorker(userData);
      console.log("Worker user created:", userResponse.data);

      // Step 2: Create worker profile with additional details
      const workerData = {
        ...formData,
        specialization: [formData.specialization], // Convert string to array
        userId: userResponse.data.id, // Link to user account
        firebaseUid: userResponse.data.firebaseUid,
        departmentId: userProfile?.departmentId,
        departmentName: userProfile?.department,
        createdBy: userProfile?.id,
        createdByName: userProfile?.name,
      };

      console.log("Creating worker profile...");
      await workerAPI.createWorker(workerData);

      setSuccess(
        `Worker created successfully! Default password: ChangeMe123! (Worker should change this on first login)`
      );
      setCreateDialog(false);
      resetForm();
      await fetchWorkers();
    } catch (error) {
      console.error("Error creating worker:", error);
      setError(
        `Failed to create worker: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditWorker = async () => {
    if (
      !selectedWorker ||
      !formData.name ||
      !formData.email ||
      !formData.phone
    ) {
      setError("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      // Prepare the data for backend, converting specialization to array
      const updateData = {
        ...formData,
        specialization: formData.specialization
          ? [formData.specialization]
          : ["General Maintenance"],
      };

      console.log("Sending worker update data:", updateData);
      await workerAPI.updateWorker(selectedWorker.id, updateData);
      setSuccess("Worker updated successfully");
      setEditDialog(false);
      setSelectedWorker(null);
      resetForm();
      await fetchWorkers();
    } catch (error) {
      console.error("Error updating worker:", error);
      setError("Failed to update worker");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteWorker = async (workerId, workerName) => {
    if (
      !window.confirm(`Are you sure you want to delete worker "${workerName}"?`)
    ) {
      return;
    }

    try {
      await workerAPI.deleteWorker(workerId);
      setSuccess("Worker deleted successfully");
      await fetchWorkers();
    } catch (error) {
      console.error("Error deleting worker:", error);
      setError("Failed to delete worker");
    }
  };

  const handleResetPassword = async (workerId, workerName, workerEmail) => {
    if (
      !window.confirm(
        `Are you sure you want to reset the password for worker "${workerName}"?\nA new temporary password will be generated.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const response = await userAPI.resetWorkerPassword(workerId);

      // Show the new password in an alert dialog
      const newPassword = response.tempPassword;
      alert(
        `Password reset successful!\n\nWorker: ${workerName}\nEmail: ${workerEmail}\nNew Password: ${newPassword}\n\nPlease share this password securely with the worker.\nThey will be required to change it on first login.`
      );

      setSuccess(`Password reset successfully for ${workerName}`);
      await fetchWorkers();
    } catch (error) {
      console.error("Error resetting password:", error);
      setError(`Failed to reset password for ${workerName}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      universityId: "",
      phone: "",
      specialization: "",
      availability: "available",
      department: "",
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setError("");
    setCreateDialog(true);
  };

  const openEditDialog = (worker) => {
    setSelectedWorker(worker);
    setFormData({
      name: worker.name || "",
      email: worker.email || "",
      phone: worker.phone || "",
      specialization: Array.isArray(worker.specialization)
        ? worker.specialization.join(", ")
        : worker.specialization || "",
      availability: worker.availability || "available",
      department: worker.department || "",
    });
    setError("");
    setEditDialog(true);
  };

  const openViewDialog = (worker) => {
    setSelectedWorker(worker);
    setViewDialog(true);
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case "available":
        return "success";
      case "busy":
        return "warning";
      case "unavailable":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Worker Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage maintenance workers and their profiles
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Tooltip title="Refresh Workers">
            <IconButton onClick={fetchWorkers} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Worker
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="body2"
                  >
                    Total Workers
                  </Typography>
                  <Typography variant="h4">{workers.length}</Typography>
                </Box>
                <BuildIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="body2"
                  >
                    Available
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {
                      workers.filter((w) => w.availability === "available")
                        .length
                    }
                  </Typography>
                </Box>
                <BuildIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="body2"
                  >
                    Busy
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {workers.filter((w) => w.availability === "busy").length}
                  </Typography>
                </Box>
                <BuildIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="body2"
                  >
                    Unavailable
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {
                      workers.filter((w) => w.availability === "unavailable")
                        .length
                    }
                  </Typography>
                </Box>
                <BuildIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Workers Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Availability</TableCell>
                <TableCell>Department</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : workers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No workers found. Click "Add Worker" to create your first
                      worker profile.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                workers.map((worker) => (
                  <TableRow key={worker.id}>
                    <TableCell>{worker.name}</TableCell>
                    <TableCell>{worker.email}</TableCell>
                    <TableCell>{worker.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          Array.isArray(worker.specialization)
                            ? worker.specialization.join(", ")
                            : worker.specialization || "General"
                        }
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={worker.availability}
                        color={getAvailabilityColor(worker.availability)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {worker.department || userProfile?.department}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => openViewDialog(worker)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Worker">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(worker)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Worker">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            handleDeleteWorker(worker.id, worker.name)
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleResetPassword(
                              worker.id,
                              worker.name,
                              worker.email
                            )
                          }
                        >
                          <LockIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Worker Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Worker</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="University ID"
              value={formData.universityId}
              onChange={(e) =>
                setFormData({ ...formData, universityId: e.target.value })
              }
              margin="normal"
              required
              placeholder="e.g., W001, EMP123, etc."
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Specialization</InputLabel>
              <Select
                value={formData.specialization}
                onChange={(e) =>
                  setFormData({ ...formData, specialization: e.target.value })
                }
                label="Specialization"
              >
                {specializations.map((spec) => (
                  <MenuItem key={spec} value={spec}>
                    {spec}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Availability</InputLabel>
              <Select
                value={formData.availability}
                onChange={(e) =>
                  setFormData({ ...formData, availability: e.target.value })
                }
                label="Availability"
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="busy">Busy</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateWorker}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? "Creating..." : "Create Worker"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Worker Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Worker</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Specialization</InputLabel>
              <Select
                value={formData.specialization}
                onChange={(e) =>
                  setFormData({ ...formData, specialization: e.target.value })
                }
                label="Specialization"
              >
                {specializations.map((spec) => (
                  <MenuItem key={spec} value={spec}>
                    {spec}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Availability</InputLabel>
              <Select
                value={formData.availability}
                onChange={(e) =>
                  setFormData({ ...formData, availability: e.target.value })
                }
                label="Availability"
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="busy">Busy</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleEditWorker}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Update Worker"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Worker Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Worker Details</DialogTitle>
        <DialogContent>
          {selectedWorker && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedWorker.name}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1">
                    {selectedWorker.email}
                  </Typography>
                  <IconButton
                    size="small"
                    href={`mailto:${selectedWorker.email}`}
                  >
                    <EmailIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Phone
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1">
                    {selectedWorker.phone}
                  </Typography>
                  <IconButton size="small" href={`tel:${selectedWorker.phone}`}>
                    <PhoneIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Specialization
                </Typography>
                <Typography variant="body1">
                  {selectedWorker.specialization}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Availability
                </Typography>
                <Chip
                  label={selectedWorker.availability}
                  color={getAvailabilityColor(selectedWorker.availability)}
                  size="small"
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Department
                </Typography>
                <Typography variant="body1">
                  {selectedWorker.department || userProfile?.department}
                </Typography>
              </Box>
              {selectedWorker.createdAt && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(
                      selectedWorker.createdAt?.toDate?.() ||
                        selectedWorker.createdAt
                    ).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkerManagement;
